// Liga o coletor BLE, a fila de envio e a API.
//
// Ordem que nao muda: a leitura entra na fila ANTES de tentar a rede. Se o
// tablet desligar no meio, a medida esta guardada.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Coletor, type StatusAparelho, type TipoDeAparelho } from '../ble/coletor';
import { traduzirLinkSun, traduzirYk, type LeituraParaEnviar } from '../ble/traduzir';
import { chamar, ErroApi } from '../api';
import { FilaDeEnvio, ArmazenamentoIndexedDB, type ResultadoEnvio } from './fila';

const INTERVALO_ESCOAR_MS = 20_000;

export interface AparelhoCadastrado {
  id: string;
  apelido: string;
  tipo: string;
  nomeBle: string | null;
}

export function useColeta(
  pacienteId: string | null,
  aparelhos: AparelhoCadastrado[],
  aoGravar: () => void,
) {
  const [status, setStatus] = useState<StatusAparelho[]>([]);
  const [naFila, setNaFila] = useState(0);
  // Quantos o navegador lembra DE FATO — ver o comentario em Coletor.temGetDevices.
  const [lembrados, setLembrados] = useState<number | null>(null);
  const coletorRef = useRef<Coletor | null>(null);
  const filaRef = useRef<FilaDeEnvio | null>(null);

  // Guarda o callback em ref para o coletor nao ser recriado a cada render —
  // recriar derrubaria as conexoes BLE.
  const aoGravarRef = useRef(aoGravar);
  aoGravarRef.current = aoGravar;
  const pacienteRef = useRef(pacienteId);
  pacienteRef.current = pacienteId;

  // Sem o aparelho vinculado, a leitura chega ao monitor como "Bluetooth" sem
  // dizer de qual aparelho — e a regra de "ao vivo" da pulseira depende de saber
  // o tipo do aparelho que mandou o dado.
  const aparelhosRef = useRef(aparelhos);
  aparelhosRef.current = aparelhos;
  const idDoAparelho = (tipo: TipoDeAparelho): string | undefined =>
    aparelhosRef.current.find((a) => a.tipo === tipo)?.id;

  const atualizarContagem = useCallback(async () => {
    const pendentes = await filaRef.current?.pendentes();
    setNaFila(pendentes?.length ?? 0);
  }, []);

  useEffect(() => {
    if (!Coletor.suportado()) return;

    const fila = new FilaDeEnvio(
      new ArmazenamentoIndexedDB(),
      async (leitura: LeituraParaEnviar): Promise<ResultadoEnvio> => {
        const paciente = pacienteRef.current;
        if (!paciente) return 'sem_rede';
        try {
          await chamar('/leituras', { metodo: 'POST', corpo: { pacienteId: paciente, ...leitura } });
          return 'enviado';
        } catch (erro) {
          // 4xx e recusa do servidor: retentar nao muda nada e travaria a fila.
          // Qualquer outra coisa e rede — a medida espera.
          if (erro instanceof ErroApi && erro.statusCode >= 400 && erro.statusCode < 500) return 'recusado';
          return 'sem_rede';
        }
      },
    );
    filaRef.current = fila;

    const coletor = new Coletor(
      async (tipo, leituras) => {
        const paciente = pacienteRef.current;
        if (!paciente) return;
        const aparelhoId = idDoAparelho(tipo);
        for (const leitura of leituras) await fila.enfileirar(paciente, { ...leitura, aparelhoId });
        await fila.escoar();
        await atualizarContagem();
        aoGravarRef.current();
      },
      setStatus,
    );
    coletorRef.current = coletor;
    setStatus(coletor.listarStatus());

    void coletor.reconectarConhecidos();
    void atualizarContagem();
    void Coletor.quantosLembrados().then(setLembrados);

    // A fila tambem escoa sozinha: pode haver medida presa de quando a rede caiu.
    const relogio = setInterval(async () => {
      await fila.escoar();
      await atualizarContagem();
    }, INTERVALO_ESCOAR_MS);

    const aoVoltarRede = () => { void fila.escoar().then(atualizarContagem); };
    window.addEventListener('online', aoVoltarRede);

    return () => {
      clearInterval(relogio);
      window.removeEventListener('online', aoVoltarRede);
      coletor.desligarTudo();
    };
  }, [atualizarContagem]);

  const parear = useCallback((tipo: TipoDeAparelho) => coletorRef.current?.parear(tipo), []);
  const desligar = useCallback((tipo: TipoDeAparelho) => coletorRef.current?.desligar(tipo), []);

  /**
   * Injeta um quadro pelo MESMO caminho de um aparelho real: traduz, enfileira,
   * envia. Serve para provar o fluxo inteiro sem o aparelho na mao.
   *
   * So existe em desenvolvimento. Num sistema que alguem vai olhar para decidir
   * se liga para a enfermeira, dado inventado nao pode chegar perto de producao.
   */
  const simular = useCallback(async (tipo: TipoDeAparelho) => {
    if (!import.meta.env.DEV) return;
    const paciente = pacienteRef.current;
    const fila = filaRef.current;
    if (!paciente || !fila) return;

    const agora = new Date();
    let leituras: LeituraParaEnviar[] = [];

    if (tipo === 'glicosimetro') {
      // Bytes reais capturados na Etapa 0, conferidos contra o visor: 213 mg/dL.
      const l = traduzirLinkSun(QUADRO_LINKSUN, agora);
      if (l) leituras = [l];
    } else if (tipo === 'pressao') {
      // Medida real do YK: 115/75, pulso 96.
      leituras = traduzirYk(QUADRO_YK, agora).leituras;
    } else {
      leituras = [{ tipo: 'frequencia_cardiaca', valor: 88 + Math.floor(Math.random() * 12), origem: 'ble', medidoEm: agora.toISOString() }];
    }

    const aparelhoId = idDoAparelho(tipo);
    for (const leitura of leituras) await fila.enfileirar(paciente, { ...leitura, aparelhoId });
    await fila.escoar();
    await atualizarContagem();
    aoGravarRef.current();
  }, [atualizarContagem]);

  return {
    status,
    naFila,
    parear,
    desligar,
    simular,
    podeSimular: import.meta.env.DEV,
    suportado: Coletor.suportado(),
    // Só conta como "lembra" se a permissão sobreviveu de fato a algum reload.
    lembraAparelhos: Coletor.temGetDevices() && lembrados !== 0,
    lembrados,
  };
}

const bytes = (hex: string) => new Uint8Array(hex.split(' ').map((b) => parseInt(b, 16)));
const QUADRO_LINKSUN = bytes('55 0c 03 18 0a 01 0a 06 00 d5 00 6e');
const QUADRO_YK = bytes('81 73 4b 60 00 00 19 04 15 02 29 00 00 00 00');
